import {
  ListTable,
  ModalMessage,
  LoadingSpinner,
  PaginationMenu,
  SortDropdown,
} from "../../components";
import { Container, Row, Col } from "react-bootstrap";
import { useEffect, useState } from "react";
import { adminFetchOrdersApi } from "../../api/ordersApi.js";
import { getStorageToken, useLogout } from "../../utilities";
import { useSelector } from "react-redux";


export default function AdminOrdersList() {
  // displays all orders as a list
  // admin can sort/filter the list

  const tableHeaders = [
    "Date",
    "User",
    "Total",
    "Paid",
    "Delivered",
    "Cancelled",
  ];
  const fieldNames = [
    "createdAt",
    ["user", "name"],
    ["orderTotal", "cartSubtotal"],
    "isPaid",
    "isDelivered",
    "isCancelled",
  ];
  
  const initialPaginationState = {
    pageNr: 1,
    totalNrOfPages: 1,
  };
  
  const sortOptions = [
    { value: "createdAt_-1", label: "Date: Newest" },
    { value: "createdAt_1", label: "Date: Oldest" },
    { value: "user.name_1", label: "User: A-Z" },
    { value: "user.name_-1", label: "User: Z-A" },
    { value: "orderTotal.cartSubtotal_-1", label: "Total: High To Low" },
    { value: "orderTotal.cartSubtotal_1", label: "Total: Low To High" },
  ];
  
  const filterOptions = [
    { value: "", label: "None"},
    { value: "isPaid_1", label: "Paid" },
    { value: "isPaid_0", label: "Not Paid" },
    { value: "isDelivered_1", label: "Delivered" },
    { value: "isDelivered_0", label: "Not Delivered" },
    { value: "isCancelled_1", label: "Cancelled" },
    { value: "refunded_1", label: "Refunded" },
    { value: "returned_1", label: "Returned" },
  ];

  const logout = useLogout();
  const [orders, setOrders] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [paginationState, setPaginationState] = useState(
    {...initialPaginationState}
  );
  const [sortOption, setSortOption] = useState(sortOptions[0]?.value);
  const [filterOption, setFilterOption] = useState(filterOptions[0]?.value);

  const { stayLoggedIn } = useSelector((state) => state.user);

  useEffect(() => {
  const abortController = new AbortController();

    const fetchOrders = async (pageNr = null) => {
      // returns array with jsons
      setIsLoading(true);
      try {
        const token = getStorageToken(stayLoggedIn);
        const res = await adminFetchOrdersApi(
          token,
          abortController.signal,
          pageNr,
          sortOption, 
          filterOption
        );
        return res; 
      } catch (error) {
        if (error?.status === 401) {
          //unautorized, eg token expired
          logout();
        }
        setErrorMsg("Could not load data. Please try again later.");
      } finally {
        setIsLoading(false);
        window.scrollTo(0, 0);
      }
    };
      const pageNr = paginationState?.pageNr || 1;
      fetchOrders(pageNr)
      .then((res) => {
        setOrders(res?.orders);
        setPaginationState({
          pageNr: res?.currPageNr || 1,
          totalNrOfPages: res?.totalNrOfPages || 1,
        });
      })
      .catch((error) =>
        setErrorMsg("Could not load data. Please try again later.")
      );
    return () => {
      abortController.abort();
    };
  }, [filterOption, sortOption, stayLoggedIn, paginationState?.pageNr, logout]);


  const handleChangePageNr = (newPageNr) => {
    if (newPageNr < 1) {
      newPageNr = 1;
    } else if (newPageNr > paginationState.totalNrOfPages) {
      newPageNr = paginationState.totalNrOfPages;
    }
    setPaginationState({ ...paginationState, pageNr: newPageNr });
  };

  return (
    <Container className="mt-5">
      {errorMsg && (
        <ModalMessage
          type="danger"
          message={errorMsg}
          setErrorMsg={setErrorMsg}
        />
      )}
      {successMsg && (
        <ModalMessage
          autoClose={true}
          type="success"
          message={successMsg}
          setSuccessMsg={setSuccessMsg}
        />
      )}
      <h1>User Orders</h1>
      {isLoading ? <LoadingSpinner /> : null}
      
      {/* SORT & FILTER MENU */}
      {orders && <Row xs={2} md={3} lg={4} className="mt-5 mb-1">
        <Col>
          <SortDropdown
            sortOptions={sortOptions}
            handleChangeSortKey={setSortOption}
          />
        </Col>
        <Col>
          <SortDropdown
            sortOptions={filterOptions}
            heading="Filter By"
            handleChangeSortKey={setFilterOption}
          />
        </Col>
      </Row>}
      {orders && (
        <ListTable
          dataArray={orders}
          headers={tableHeaders}
          fieldNames={fieldNames}
          view={`/admin/order-details`}
          admin={true}
        />
      )}
      {/* PAGINATION MENU */}
      {orders && orders.length > 0 && (
        <PaginationMenu
          pageNr={paginationState.pageNr}
          totalNrOfPages={paginationState.totalNrOfPages}
          handleChangePageNr={handleChangePageNr}
        />
      )}
    </Container>
  );
}